import { useEffect, useRef, useState } from 'react'
import { getAudioMedia } from '../../utils/audio'
import AudioMeter from './AudioMeter'

interface Props {
  deviceId: string
}

export default function DevicePreview({ deviceId }: Props) {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState(false)
  // Keep the live stream in a ref so the effect's cleanup can stop the
  // tracks even when the React state hasn't propagated yet. The previous
  // implementation read `stream` from the closure captured at mount —
  // which is always `null` because setStream resolves AFTER the closure
  // was created — so every dropdown render leaked the mic capture. On a
  // device list with 3 mics and StrictMode dev's double-mount that's
  // up to 6 simultaneous opens of the same physical device, which
  // Windows tends to "succeed" by handing out silent duplicate streams.
  // The actual live call mic published in parallel then publishes
  // silence to the SFU — exactly the OSPROD "UI connected, no audio"
  // symptom users were hitting after touching the device picker.
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    let cancelled = false
    const start = async () => {
      try {
        const s = await getAudioMedia({
          deviceId: { ideal: deviceId },
          video: false,
        })
        if (cancelled) {
          // Mount was already torn down before getUserMedia resolved —
          // stop the freshly-opened tracks instead of stashing them.
          s.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = s
        setStream(s)
      } catch {
        if (!cancelled) setError(true)
      }
    }
    start()
    return () => {
      cancelled = true
      const live = streamRef.current
      if (live) {
        live.getTracks().forEach((t) => t.stop())
      }
      streamRef.current = null
    }
  }, [deviceId])

  if (error) return <span className="text-muted-foreground text-[10px]">—</span>
  if (!stream) return <span className="text-muted-foreground text-[10px]">...</span>

  return <AudioMeter stream={stream} size="sm" bars={3} />
}
